import { useCreateChatbot } from '@/components/Ingest/useCreateChatbot';
import { useUpgrade } from '@/components/Modals/UpgradeModal';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { DropdownMenuContent } from '@/components/ui/DropdownMenu';
import { Icon } from '@/components/ui/Icon';
import { useAuth } from '@/context/AuthContext';
import { useCreateEmptyFolder } from '@/hooks/useCreateEmptyFolder';
import useDowntimeFlag from '@/hooks/useDowntimeFlag';
import useLimits from '@/hooks/useLimits';
import useNewNote from '@/hooks/useNewNote';
import useAnalytics from '@/lib/analytics/useAnalytics';
import cuid from 'cuid';
import { CircleHelp, FolderOpen, Mic, PenLine, Plus } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import ButtonCard from '../ButtonCard';

export const CreateItems = ({
  setNewNoteLoading,
  landingPage = false,
  setIsLoading,
}: {
  setNewNoteLoading?: (loading: boolean) => void;
  landingPage?: boolean;
  setIsLoading?: (loading: boolean) => void;
}) => {
  const { isDowntime } = useDowntimeFlag();
  const { usage, exceededLimits, optimisticIncreaseRecordingLimit } =
    useLimits();
  const { userPlanDetails } = useAuth();
  const { showUpgrade } = useUpgrade();
  const { track } = useAnalytics();
  const { user } = useAuth();
  const router = useRouter();

  const { createChatbot } = useCreateChatbot({
    onSuccess: (data) => {
      if (!data) return;
      router.push(`/b/${data.id}`);
      track('chatbotCreated', {
        type: 'RECORDING',
        chatbotId: data.id,
      });
      track('recording_created');
      optimisticIncreaseRecordingLimit();
    },
    onSettled: () => {
      setIsLoading?.(false);
    },
  });

  const { createNote } = useNewNote({
    onSuccess: (data) => {
      if (!data) return;
      router.push(`/b/${data.id}`);
      track('newCreateNoteClicked');
      setIsLoading?.(false);
    },
    onError: (_) => {
      setNewNoteLoading?.(false);
    },
    onSettled: () => {
      setIsLoading?.(false);
    },
  });

  const { createEmptyFolder } = useCreateEmptyFolder();

  const handleNoteOptionClick = () => {
    if (!user) return;
    track(landingPage ? 'landing_button_clicked' : 'plus_menu_button_clicked', {
      button: 'create_note',
    });
    createNote(undefined);
    setIsLoading?.(true);
    setNewNoteLoading?.(true);
  };

  const handleRecordingClick = () => {
    if (isDowntime) return;
    if (
      userPlanDetails?.planName === 'Free' &&
      usage?.recordingCount &&
      exceededLimits?.isRecordingLimitExceeded
    ) {
      showUpgrade('recordingLimit');
      track('recording_attempt_free_plan', {
        userId: user?.id,
      });
      return;
    }
    track(landingPage ? 'landing_button_clicked' : 'plus_menu_button_clicked', {
      button: 'create_recording',
    });
    createChatbot({
      name: 'New Recording',
      type: 'RECORDING',
    });
    setIsLoading?.(true);
  };

  const handleGroupOptionClick = () => {
    if (isDowntime) return;

    createEmptyFolder({ id: cuid() });
  };

  return (
    <>
      <DropdownMenuItem onClick={handleNoteOptionClick}>
        <Icon as={PenLine} />
        Note
      </DropdownMenuItem>

      <DropdownMenuItem onClick={handleRecordingClick}>
        <Icon as={Mic} />
        Recording
      </DropdownMenuItem>
      <DropdownMenuItem onClick={handleGroupOptionClick}>
        <Icon as={FolderOpen} />
        Folder
      </DropdownMenuItem>
    </>
  );
};

export const CreateButton = ({
  setNewNoteLoading,
}: {
  setNewNoteLoading: (loading: boolean) => void;
}) => {
  const [openMenu, setOpenMenu] = useState(false);
  const { track } = useAnalytics();

  return (
    <DropdownMenu
      open={openMenu}
      onOpenChange={(open) => {
        setOpenMenu(open);
      }}
    >
      <DropdownMenuTrigger asChild className="data-[state=open]:bg-background">
        <div>
          <ButtonCard
            className={
              openMenu ? 'bg-popover-hover/40 dark:bg-popover-hover' : ''
            }
            label="Create"
            icon={Plus}
          />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        className="w-[224px] p-0 text-sm leading-[100%] tracking-[-0.32px]"
      >
        <div className="p-0.5">
          <CreateItems setNewNoteLoading={setNewNoteLoading} landingPage />
        </div>
        <div className="h-[1px] bg-border" />
        <div className="p-0.5">
          <DropdownMenuItem className="text-textGray4">
            <Link
              href="/docs/import/file-types"
              target="_blank"
              className="flex flex-row gap-2 items-center"
              onClick={() => {
                track('plus_menu_button_clicked', {
                  button: 'create_button_learn_file_types',
                });
              }}
            >
              <Icon as={CircleHelp} />
              Learn about file types
            </Link>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
